// Games with a blunder or a miss of a kind: one group of the Blunders or
// Misses screen, named `<kind>-<dimension>-<value>` (`blunder-motif-fork`,
// `blunder-motif-fork/knight`, `miss-clock-under1`; `mistakeKeys` in
// `@chess/api-mistakes`). Like `blunder` it lives in the scan's facts
// (`mistakes`), which the filter does not read: whoever applies the filter
// resolves it first into `ids` (the game store's `resolveFilter`, with
// `gameMistakeKeys` and the `mistakes.motifs` service, the same the screens
// count with). Unresolved, it matches no game. The menu offers the motif
// families (Pin, Fork, Mate …), the severity, the standing before the move
// and the clock, kind by kind; a family's variations (`pin/queen`), the
// material and the week are reached by the screens' links and listed under
// "From a link" while chosen.
import { oneOf, type FilterCriterion, type FilterOption } from '../criterion.ts';
import { MISTAKE_KEYS, MISTAKE_KIND_LABEL, mistakeKeyLabel, MOVE_KINDS, parseMistakeKey, type MistakeDimension } from '@chess/api-mistakes';

/** The dimensions the menu lists (of `motif`, the families only); the others only a link sets. */
const IN_MENU: readonly MistakeDimension[] = ['motif', 'severity', 'standing', 'clock'];
const inMenu = (dimension: MistakeDimension, value: string) => IN_MENU.includes(dimension) && !(dimension === 'motif' && value.includes('/'));

/** "Blunders: walked into a fork", as the filter bar writes a chosen option. */
export function mistakeChipLabel(key: string): string | undefined {
  const parts = parseMistakeKey(key);
  const label = mistakeKeyLabel(key);
  if (!parts || !label) return undefined;
  return `${MISTAKE_KIND_LABEL[parts.kind]}: ${label.charAt(0).toLowerCase()}${label.slice(1)}`;
}

const OPTIONS: ReadonlyArray<FilterOption<string>> = MOVE_KINDS.flatMap((kind) =>
  MISTAKE_KEYS.filter((k) => {
    const p = parseMistakeKey(k)!;
    return p.kind === kind && inMenu(p.dimension, p.value);
  }).map((k) => [k, mistakeKeyLabel(k)!, MISTAKE_KIND_LABEL[kind]] as FilterOption<string>),
);

export const mistake: FilterCriterion<'mistake'> = {
  key: 'mistake',
  query: 'mistake',
  many: true,
  parse: oneOf(MISTAKE_KEYS as readonly string[]),
  format: (v) => v,
  matches: (_game, _v, filter) => !!filter.ids,
  control: {
    kind: 'choice',
    order: 61,
    more: true,
    label: 'Blunder or miss',
    options: OPTIONS,
    linkedLabel: mistakeChipLabel,
  },
};
